/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useRouter } from "vue-router";

import cashierImageURL from "@app/images/landing/catering-hero-WEB.jpeg";
// Restaurant types with icons
const restaurantTypes = [
  { id: "cafe", name: "Café", icon: "coffee" },
  { id: "fastfood", name: "Fast Food", icon: "fastfood" },
  { id: "finedining", name: "Fine Dining", icon: "restaurant" },
  { id: "bakery", name: "Bakery", icon: "bakery_dining" },
  { id: "bar", name: "Bar", icon: "sports_bar" },
  { id: "buffet", name: "Buffet", icon: "dinner_dining" },
  { id: "foodtruck", name: "Food Truck", icon: "local_shipping" },
];

export const OnboardingPage = defineComponent({
  name: "OnboardingPage",
  setup() {
    const currentStep = ref(1);
    const router = useRouter();
    const isLoading = ref(false);
    const verificationSent = ref(false);

    // Form data
    const restaurantType = ref("");
    const restaurantInfo = ref({
      name: "",
      email: "",
      address: "",
      country: "",
      phone: "",
      registrationNumber: "",
    });
    const verificationCode = ref("");

    const goToNextStep = () => {
      if (currentStep.value === 1 && !restaurantType.value) {
        return; // Don't proceed if restaurant type not selected
      }

      if (currentStep.value === 2) {
        // Validate form before proceeding
        if (
          !restaurantInfo.value.name ||
          !restaurantInfo.value.email ||
          !restaurantInfo.value.phone
        ) {
          return;
        }

        // Simulate sending verification code
        isLoading.value = true;
        setTimeout(() => {
          isLoading.value = false;
          verificationSent.value = true;
          currentStep.value += 1;
        }, 1500);
        return;
      }

      currentStep.value += 1;
    };

    const goToPreviousStep = () => {
      currentStep.value -= 1;
    };

    const submitVerification = () => {
      if (!verificationCode.value || verificationCode.value.length !== 6) {
        return;
      }

      isLoading.value = true;
      // Simulate verification and account creation
      setTimeout(() => {
        isLoading.value = false;
        // Redirect to login page after successful onboarding
        router.push("/login");
      }, 2000);
    };

    const selectRestaurantType = (type: string) => {
      restaurantType.value = type;
    };

    return () => {
      return (
        <div class="flex-1 flex flex-col md:flex-row">
          {/* Header with logo */}
          <div
            style=""
            class="flex bg-cover p-6 flex-1 flex-col justify-center items-center "
          >
            <img
              class="max-w-full mx-auto rounded-3xl img-holder"
              src={cashierImageURL}
            />
          </div>

          {/* Main content */}
          <div class="flex-1 flex flex-col  overflow-y-auto">
            <div class=" rounded-lg p-4 max-w-3xl w-full ">
              {/* Progress indicator */}
              <div class="w-full mb-8 border-b  py-8">
                <ul class="steps w-full">
                  <li
                    class={`step ${
                      currentStep.value >= 1 ? "step-primary" : ""
                    }`}
                  >
                    Restaurant Type
                  </li>
                  <li
                    class={`step ${
                      currentStep.value >= 2 ? "step-primary" : ""
                    }`}
                  >
                    Restaurant Info
                  </li>
                  <li
                    class={`step ${
                      currentStep.value >= 3 ? "step-primary" : ""
                    }`}
                  >
                    Verification
                  </li>
                </ul>
              </div>

              {/* Step 1: Restaurant Type Selection */}
              {currentStep.value === 1 && (
                <div class="">
                  <h2 class="text-2xl  text-center mb-6">
                    What type of restaurant do you have?
                  </h2>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {restaurantTypes.map((type) => (
                      <div
                        key={type.id}
                        class={`flex flex-col items-center justify-center p-4 rounded-lg cursor-pointer transition-all hover:bg-primary hover:text-primary-content border  ${
                          restaurantType.value === type.id
                            ? "bg-primary text-primary-content"
                            : ""
                        }`}
                        onClick={() => selectRestaurantType(type.id)}
                      >
                        <span class="material-symbols-outlined text-4xl mb-2">
                          {type.icon}
                        </span>
                        <span class="text-center">{type.name}</span>
                      </div>
                    ))}
                  </div>
                  <div class="flex justify-end">
                    <button
                      class={`btn btn-primary ${
                        !restaurantType.value ? "btn-disabled" : ""
                      }`}
                      onClick={goToNextStep}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Restaurant Information */}
              {currentStep.value === 2 && (
                <div class="">
                  <h2 class="text-2xl text-center mb-6">
                    Tell us about your restaurant
                  </h2>
                  <div class="max-w-md mx-auto">
                    <div class="form-control mb-4">
                      <label class="label">
                        <span class="label-text">Restaurant Name*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter restaurant name"
                        class="input input-bordered w-full"
                        v-model={restaurantInfo.value.name}
                      />
                    </div>

                    <div class="form-control mb-4">
                      <label class="label">
                        <span class="label-text">Email Address*</span>
                      </label>
                      <input
                        type="email"
                        placeholder="Enter email address"
                        class="input input-bordered w-full"
                        v-model={restaurantInfo.value.email}
                      />
                    </div>

                    <div class="form-control mb-4">
                      <label class="label">
                        <span class="label-text">Phone Number*</span>
                      </label>
                      <input
                        type="tel"
                        placeholder="Enter phone number"
                        class="input input-bordered w-full"
                        v-model={restaurantInfo.value.phone}
                      />
                    </div>

                    <div class="form-control mb-4">
                      <label class="label">
                        <span class="label-text">Address</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter address"
                        class="input input-bordered w-full"
                        v-model={restaurantInfo.value.address}
                      />
                    </div>

                    <div class="form-control mb-4">
                      <label class="label">
                        <span class="label-text">Country</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter country"
                        class="input input-bordered w-full"
                        v-model={restaurantInfo.value.country}
                      />
                    </div>

                    <div class="form-control mb-6 hidden">
                      <label class="label">
                        <span class="label-text">
                          Company Registration Number
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter registration number"
                        class="input input-bordered w-full"
                        v-model={restaurantInfo.value.registrationNumber}
                      />
                    </div>

                    <div class="flex justify-between">
                      <button
                        class="btn btn-outline"
                        onClick={goToPreviousStep}
                      >
                        Back
                      </button>
                      <button
                        class={`btn btn-primary ${
                          !restaurantInfo.value.name ||
                          !restaurantInfo.value.email ||
                          !restaurantInfo.value.phone
                            ? "btn-disabled"
                            : ""
                        }`}
                        onClick={goToNextStep}
                      >
                        {isLoading.value ? (
                          <span class="loading loading-spinner"></span>
                        ) : (
                          "Next"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Verification */}
              {currentStep.value === 3 && (
                <div class="">
                  <h2 class="text-2xl font-bold text-center mb-6">
                    Verify your account
                  </h2>
                  <div class="max-w-md mx-auto text-center">
                    <div class="mb-6">
                      <p class="mb-4">
                        We've sent a 6-digit verification code to:
                      </p>
                      <div class="text-lg font-medium">
                        {restaurantInfo.value.email}
                      </div>
                      <div class="text-sm text-neutral mt-2">
                        Please check your inbox and enter the code below
                      </div>
                    </div>

                    <div class="form-control mb-8">
                      <input
                        type="text"
                        placeholder="Enter 6-digit code"
                        class="input input-bordered w-full text-center text-xl tracking-widest"
                        maxlength="6"
                        v-model={verificationCode.value}
                      />
                    </div>

                    <div class="flex flex-col gap-4 items-center">
                      <button
                        class={`btn btn-primary w-full ${
                          !verificationCode.value ||
                          verificationCode.value.length !== 6
                            ? "btn-disabled"
                            : ""
                        }`}
                        onClick={submitVerification}
                      >
                        {isLoading.value ? (
                          <span class="loading loading-spinner"></span>
                        ) : (
                          "Complete Setup"
                        )}
                      </button>

                      <button class="btn btn-link btn-sm">Resend Code</button>

                      <button
                        class="btn btn-outline btn-sm mt-4"
                        onClick={goToPreviousStep}
                      >
                        Back to Restaurant Information
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
  },
});
